import React, { useEffect, useState } from "react";
import { Bookmark, BookmarkPlus, Trash2, Loader2, TrendingUp, TrendingDown, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  fetchSavedAnalyses,
  createSavedAnalysis,
  deleteSavedAnalysis,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtNum, fmtPct } from "./helpers";

export const SaveAnalysisButton = ({ data, onSaved, onUpsellClick }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const isPro = user?.tier === "pro";

  const handleClick = async () => {
    if (!user) {
      toast.message("Sign in to save analyses", {
        description: "Pro users keep a journal of every AI call to compare with current price.",
      });
      return;
    }
    if (!isPro) {
      onUpsellClick?.();
      return;
    }
    if (!data?.symbol || !data?.quote?.price) {
      toast.error("Analysis not ready yet");
      return;
    }
    setSaving(true);
    try {
      await createSavedAnalysis({
        symbol: data.symbol,
        label: data.label,
        sector: data.sector,
        entry_price: data.quote.price,
        directional_bias: data.ai?.directional_bias,
        conviction_pct: data.ai?.conviction_pct,
        thesis: data.ai?.thesis,
        trade_ideas: data.ai?.trade_ideas || [],
        ai_source: data.source || "ai",
      });
      toast.success(`Saved ${data.symbol}`, {
        description: "Track this call's performance below.",
      });
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not save";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={saving}
      data-testid="analysis-save-btn"
      className={
        "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border transition num " +
        (isPro
          ? "border-[#FFD60A]/45 text-[#FFD60A] hover:bg-[#FFD60A]/10"
          : "border-[#FFD60A]/30 text-[#FFD60A]/70 hover:bg-[#FFD60A]/5")
      }
    >
      {saving ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : isPro ? (
        <BookmarkPlus className="w-3.5 h-3.5" />
      ) : (
        <Lock className="w-3.5 h-3.5" />
      )}
      {isPro ? "Save analysis" : "Pro · Save"}
    </button>
  );
};

export const SavedAnalysesPanel = ({ refreshKey, onPick }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isPro = user?.tier === "pro";

  const reload = () => {
    if (!isPro) return;
    setLoading(true);
    setError(null);
    fetchSavedAnalyses()
      .then((d) => setItems(d.items || []))
      .catch((err) => setError(err?.response?.data?.detail || "Could not load saved analyses"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, refreshKey]);

  const remove = async (id) => {
    try {
      await deleteSavedAnalysis(id);
      setItems((curr) => curr.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  if (!user) return null;
  if (!isPro) {
    return (
      <div
        data-testid="saved-analyses-locked"
        className="rounded-xl border border-[#FFD60A]/25 bg-gradient-to-br from-[#16110a] to-[#070710] p-5 mb-6"
      >
        <div className="flex items-center gap-2 mb-2">
          <Bookmark className="w-4 h-4 text-[#FFD60A]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFD60A]">
            Pro · Your analysis journal
          </span>
        </div>
        <p className="text-[12.5px] text-white/70 leading-snug">
          Pro users can snapshot every AI thesis + trade idea and watch how each call plays out
          against current price. Build a track record. Sharpen your edge.
        </p>
      </div>
    );
  }

  if (loading && items.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#070710] p-5 mb-6 flex items-center gap-2 text-[12px] text-white/45">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading saved analyses…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-4 mb-6 text-[12px] text-[#FF3B30]">
        {error}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div
        data-testid="saved-analyses-empty"
        className="rounded-xl border border-white/10 bg-[#070710] p-5 mb-6"
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Bookmark className="w-4 h-4 text-[#FFD60A]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFD60A]">
            Your analysis journal
          </span>
        </div>
        <p className="text-[12px] text-white/55">
          No saved analyses yet. Hit "Save analysis" on any ticker to start tracking your AI calls.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="saved-analyses-panel"
      className="rounded-xl border border-white/10 bg-[#070710] p-4 sm:p-5 mb-6"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bookmark className="w-4 h-4 text-[#FFD60A]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFD60A]">
            Your analysis journal · {items.length}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/35">
          Tap to re-analyze
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => {
          const pnl = it.pnl_pct;
          const verdict = it.verdict;
          const pnlColor = pnl == null
            ? "#A1A1AA"
            : pnl >= 0 ? "#00C805" : "#FF3B30";
          const verdictColor = verdict === "correct"
            ? "#00C805"
            : verdict === "wrong" ? "#FF3B30" : "#A1A1AA";
          return (
            <div
              key={it.id}
              data-testid={`saved-row-${it.symbol}`}
              className="rounded-md border border-white/10 bg-white/[0.02] p-3 hover:border-white/25 transition"
            >
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => onPick?.(it.symbol)}
                  className="text-left flex-1 min-w-0"
                >
                  <div className="text-[13px] font-semibold text-white num">{it.symbol}</div>
                  <div className="text-[10.5px] text-white/45 truncate">{it.label}</div>
                </button>
                <button
                  type="button"
                  onClick={() => remove(it.id)}
                  data-testid={`saved-delete-${it.symbol}`}
                  className="text-white/35 hover:text-[#FF3B30] transition shrink-0"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10.5px] text-white/65 num">
                <div>Entry: <span className="text-white/90">${fmtNum(it.entry_price)}</span></div>
                <div>
                  Now: <span className="text-white/90">{it.current_price != null ? `$${fmtNum(it.current_price)}` : "—"}</span>
                </div>
                <div className="col-span-2 flex items-center gap-1.5 mt-0.5">
                  {pnl != null && (
                    <>
                      {pnl >= 0
                        ? <TrendingUp className="w-3 h-3" style={{ color: pnlColor }} />
                        : <TrendingDown className="w-3 h-3" style={{ color: pnlColor }} />}
                      <span style={{ color: pnlColor }} className="text-[11px] font-medium">
                        {fmtPct(pnl)} since saved
                      </span>
                    </>
                  )}
                  {verdict && pnl != null && (
                    <span
                      className="ml-auto text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border"
                      style={{ borderColor: `${verdictColor}55`, color: verdictColor }}
                    >
                      {verdict}
                    </span>
                  )}
                </div>
              </div>
              {it.directional_bias && (
                <div className="mt-2 text-[10px] text-white/45">
                  Call: <span className="text-white/70">{it.directional_bias}</span>
                  {it.conviction_pct != null && <span className="num"> · {it.conviction_pct}% conv</span>}
                  <span className="ml-2">· {new Date(it.saved_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
